/* Light / dark theme manager.
 * Resolution order: explicit saved choice → OS preference (prefers-color-scheme)
 * → time of day (evening/night = dark). Sets data-theme on <html>; the dark token
 * set in scss/tokens/_colors.scss keys off it. While the user hasn't made an
 * explicit choice, the app keeps following the OS preference live. */

const KEY = 'sm_theme';

function canMatchScheme() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-color-scheme: dark)').media !== 'not all';
}

function systemPrefersDark() {
  return canMatchScheme() && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function timePrefersDark() {
  const h = new Date().getHours();
  return h >= 19 || h < 7;
}

function savedTheme() {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch (e) {
    return null;
  }
}

export function resolveInitialTheme() {
  return savedTheme()
    || (canMatchScheme() ? (systemPrefersDark() ? 'dark' : 'light')
                         : (timePrefersDark() ? 'dark' : 'light'));
}

// No theme-color meta at all: iOS Safari then derives the chrome tint from the
// page itself (status-bar area matches whatever is rendered at the top edge —
// light header on the menu, dark hero on the welcome screen). A fixed value
// can't do that per-screen and read as an alien grey band above the app; this
// also self-adapts to the dark theme with no extra bookkeeping.
// Exception: while a bottom sheet is open, utils/navLock.js temporarily sets
// theme-color to --surface-card so Safari's bar zone matches the sheet.
function setMetaThemeColor() {
  if (typeof document === 'undefined') return;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.remove();
}

export function applyTheme(theme) {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
    setMetaThemeColor(theme);
  }
}

export function getTheme() {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') || 'light';
}

export function setTheme(theme, persist = true) {
  applyTheme(theme);
  if (persist) {
    try { localStorage.setItem(KEY, theme); } catch (e) { /* ignore */ }
  }
}

export function toggleTheme() {
  const next = getTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

export function initTheme() {
  applyTheme(resolveInitialTheme());

  // Follow live OS changes only while the user hasn't picked a theme themselves.
  if (canMatchScheme()) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (!savedTheme()) applyTheme(e.matches ? 'dark' : 'light');
    };
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
  }
}

export default { initTheme, getTheme, setTheme, toggleTheme, resolveInitialTheme, applyTheme };
